import { Box, Divider, Paper, Typography } from "@mui/material";
import { pick } from "radash";
import { json, useLoaderData } from "react-router-dom";
import { get } from "../apiClient";
import PublishersList from "../features/programmatic_supply/PublishersList";
import PublishersListHeader from "../features/programmatic_supply/PublishersListHeader";
import { useScrollToTop } from "../hooks/useScrollToTop";
import useSearchParamsWithDefaults from "../hooks/useSearchParamsWithDefaults";
import useTitle from "../hooks/useTitle";

const FILTER_PARAMS = ["search"];
const PAGINATION_DEFAULTS = {
  per_page: "25",
  page: "1",
  order_direction: "desc",
  order_by: "id",
};

const searchParamsToApiParams = (searchParams) => {
  const params = Object.fromEntries(searchParams);

  return {
    ...pick(params, FILTER_PARAMS),
    ...PAGINATION_DEFAULTS,
    ...pick(params, Object.keys(PAGINATION_DEFAULTS)),
  };
};

const loader = async ({ request }) => {
  const params = searchParamsToApiParams(new URL(request.url).searchParams);

  try {
    const {
      data: publishers,
      pagination: { total_count: totalCount },
    } = await get("player", "/api/programmatic_supply/publishers", {
      params,
      signal: request.signal,
    });

    return { params, publishers, totalCount };
  } catch (error) {
    throw json(
      {
        title: "There was an error fetching publishers from the server",
        text: " Please try again. If the error is persistent, contact support.",
      },
      { status: error.status }
    );
  }
};

const ProgrammaticSupplyPublishers = () => {
  useTitle("Programmatic Supply Configuration > Publisher Configuration");
  useScrollToTop();
  const { params, publishers, totalCount } = useLoaderData();
  const [searchParams, setSearchParams] =
    useSearchParamsWithDefaults(PAGINATION_DEFAULTS);

  return (
    <>
      <Paper elevation={0} square={false} sx={{ borderRadius: 5 }}>
        <Box sx={{ px: 4, py: 2 }}>
          <Typography variant="h5">Publisher Configuration</Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 4, pb: 2 }}>
          <PublishersListHeader filters={params} />
          <PublishersList
            onSortOrderChange={(event, order) => {
              setSearchParams((prev) => ({
                ...Object.fromEntries(prev),
                order_direction: order.orderDirection,
                order_by: order.orderBy,
              }));
            }}
            publishers={publishers}
            totalCount={totalCount}
            orderBy={params.order_by}
            orderDirection={params.order_direction}
            onPageChange={(_event, page) => {
              setSearchParams((prev) => ({
                ...Object.fromEntries(prev),
                page: page + 1,
              }));
            }}
            onRowsPerPageChange={(event) => {
              setSearchParams((prev) => ({
                ...Object.fromEntries(prev),
                per_page: event.target.value,
              }));
            }}
            page={Math.max(Number(searchParams.get("page")) - 1, 0)}
            rowsPerPage={Number(searchParams.get("per_page"))}
          />
        </Box>
      </Paper>
    </>
  );
};

export { loader };

export default ProgrammaticSupplyPublishers;
